import { Location } from '@angular/common';
import { Component, computed, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, filter, firstValueFrom } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { PhysicalInventoryStateEnums } from 'src/app/enum/physical-inventory-state-enums';
import { WindowEnums } from 'src/app/enum/window-enums';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { PhysicalInventoryModel } from 'src/app/models/inventory/physical-inventory.model';
import { StoreService } from 'src/app/services/archive/store.service';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { InventoryService } from 'src/app/services/inventory/inventory.service';
import { PhysicalInventoryService } from 'src/app/services/inventory/physical-inventory.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { AdjustExcessModalComponent } from '../adjust-excess-modal/adjust-excess-modal.component';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';

@Component({
  selector: 'app-register-physical-inventory',
  templateUrl: './register-physical-inventory.component.html',
  styleUrls: ['./register-physical-inventory.component.scss'],
  standalone: false
})
export class RegisterPhysicalInventoryComponent implements OnInit {

  @ViewChild('adjustExcessModal') adjustExcessModalComponent?: CustomModalComponent | null = null;

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly branchService = inject(BranchService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly dateConverterService = inject(DateConverterService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly formControlStateService = inject(FormControlStateService);
  private readonly inventoryService = inject(InventoryService);
  private readonly location = inject(Location);
  private readonly optionWindowService = inject(OptionWindowService);
  private readonly physicalInventoryService = inject(PhysicalInventoryService);
  private readonly storeService = inject(StoreService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly userAuthenticatedService = inject(UserAuthenticatedService);
  private readonly windowService = inject(WindowService);

  readonly state = signal<number>(PhysicalInventoryStateEnums.open);
  readonly stateButtonIcon = computed(() => this.state() === PhysicalInventoryStateEnums.open ? 'pi pi-lock-open' : 'pi pi-lock');
  readonly stateButtonLabel = computed(() => this.state() === PhysicalInventoryStateEnums.open ? 'Cerrar' : 'Cerrado');
  readonly stateLabel = computed(() => this.state() === PhysicalInventoryStateEnums.open ? 'Abierto' : 'Cerrado');
  readonly stateSeverity = computed(() => this.state() === PhysicalInventoryStateEnums.open ? 'danger' : 'success');

  disabledAdjustExcessBtn = true;
  disabledAdjustMissingBtns = true;
  disabledChangeState: boolean = false;
  disabledDetail: boolean = true;
  disabledSave: boolean = false;
  formPhysicalInventory: FormGroup = this.formBuilder.group({});
  inputsForAdjustExcessModals$ = new BehaviorSubject<any>({
    branchId: 0,
    inventoryId: 0,
    storeId: 0
  });
  listBranches: any[] = [];
  listInventories: any[] = [];
  listStores: any[] = [];
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  physicalInventoryContract: PhysicalInventoryModel = new PhysicalInventoryModel();
  physicalInventoryId: number = 0;
  url: string = "physical-inventory";
  userAuthenticated: UserModel = new UserModel();
  componentAdjustExcess = AdjustExcessModalComponent;

  async ngOnInit() {
    this.validateForm(this.physicalInventoryContract);
    await this.getPermission();
    await this.loadLists();
    this.activatedRoute.params.subscribe(params => {
      this.physicalInventoryId = Object.keys(params).length > 0 ? params?.id : null;
    });
    this.handleInitialState();
  }

  async adjustExcessResponse(event: any) {
    if (event.selectedStoreId != null) {
      const accepted = await this.confirmDialogService.confirm('¿Realmente desea ajustar el inventario con sobrantes?');
      if (accepted) {
        try {
          const response = await firstValueFrom(this.physicalInventoryService.adjustWithExcess(
            this.physicalInventoryContract.physicalInventoryId, event.selectedStoreId
          ));
          this.physicalInventoryContract = response;
          this.validateForm(this.physicalInventoryContract);
          this.toastHandlerService.showToast('success', 'El inventario físico se ajustó con sobrantes exitosamente.');
        } catch (error) {
          console.error('Error al ajustar el inventario con sobrantes.', error);
        }
      }
    }
  }

  async adjustMissing() {
    try {
      const accepted = await this.confirmDialogService.confirm('¿Realmente desea ajustar el inventario con faltantes?');
      if (accepted) {
        const response = await firstValueFrom(this.physicalInventoryService.adjustWithMissing(this.physicalInventoryContract.physicalInventoryId));
        this.physicalInventoryContract = response;
        this.validateForm(this.physicalInventoryContract);
        this.toastHandlerService.showToast('success', 'El inventario físico se ajustó con faltantes exitosamente.');
      }
    } catch (error) {
      console.error('Error al ajustar el inventario con faltantes.', error);
    }
    this.physicalInventoryService.adjustWithMissing(this.physicalInventoryContract.physicalInventoryId);
  }

  async changeConsecutive(consecutive: number) {
    const inventoryId = this.formPhysicalInventory.get("inventoryId")?.value;
    const branchId = this.formPhysicalInventory.get("branchId")?.value;
    const storeId = this.formPhysicalInventory.get("storeId")?.value;

    if (inventoryId === null || inventoryId === 0) {
      this.toastHandlerService.showToast('warning', 'Seleccione un inventario.');
      return;
    }
    if (branchId === null || branchId === 0) {
      this.toastHandlerService.showToast('warning', 'Seleccione una sucursal.');
      return;
    }
    if (storeId === null || storeId === 0) {
      this.toastHandlerService.showToast('warning', 'Seleccione una bodega.');
      return;
    }
    try {
      this.physicalInventoryContract = await firstValueFrom(
        this.physicalInventoryService.getPhysicalInventoryByKeysAsync(inventoryId, branchId, storeId, consecutive)
      );
      this.physicalInventoryId = this.physicalInventoryContract.physicalInventoryId;
      this.validateForm(this.physicalInventoryContract);
      this.location.replaceState(`/physical-inventory/register/${this.physicalInventoryId}`);
    } catch (error) {
      this.physicalInventoryContract = new PhysicalInventoryModel();
      this.physicalInventoryContract.inventoryId = inventoryId;
      this.physicalInventoryContract.branchId = branchId;
      this.physicalInventoryContract.storeId = storeId;
      this.physicalInventoryId = this.physicalInventoryContract.physicalInventoryId;
      this.validateForm(this.physicalInventoryContract);
      console.error('Error al obtener el inventario físico por claves.', error);
    }
  }

  changeBranch() {
    this.formPhysicalInventory.controls['storeId'].patchValue(null);
    this.createNewRegister();
  }

  changeDate() {
    const datePhysicalInventory = this.formPhysicalInventory.get('datePhysicalInventory')!.value;
    this.formPhysicalInventory.controls['dateAdjustment'].patchValue(datePhysicalInventory);
  }

  changeInventory() {
    this.formPhysicalInventory.controls['storeId'].patchValue(null);
    this.createNewRegister();
  }

  async changeState() {
    if (this.physicalInventoryContract.physicalInventoryId !== 0 && this.physicalInventoryContract.physicalInventoryId !== null) {
      if (this.permissionUpdatable) {
        try {
          this.physicalInventoryContract = await firstValueFrom(
            this.physicalInventoryService.setPhysicalInventoryState(this.physicalInventoryContract.physicalInventoryId)
          );
          this.validateForm(this.physicalInventoryContract);
          this.toastHandlerService.showToast('success', 'El estado del inventario físico se modificó exitosamente.');
        } catch (error) {
          console.error("Error al cambiar el estado del inventario físico.", error);
        }
      } else {
        this.toastHandlerService.showToast(
          'warning',
          'El usuario no tiene permisos para modificar el inventario físico. Comuníquese con el administrador del sistema.'
        );
        return;
      }
    } else {
      this.toastHandlerService.showToast('warning', 'Debe crear un inventario físico.');
      return;
    }
  }

  createNewRegister() {
    if (this.permissionUpdatable) {
      this.physicalInventoryContract = new PhysicalInventoryModel();
      this.state.set(PhysicalInventoryStateEnums.open);
      this.formPhysicalInventory.enable();
      this.disabledChangeState = false;
      this.disabledSave = false;
      this.disabledAdjustExcessBtn = true;
      this.disabledAdjustMissingBtns = true;
      this.physicalInventoryContract.inventoryId = this.formPhysicalInventory.get("inventoryId")?.value;
      this.physicalInventoryContract.branchId = this.formPhysicalInventory.get("branchId")?.value;
      this.physicalInventoryContract.storeId = this.formPhysicalInventory.get("storeId")?.value;
      this.validateForm(this.physicalInventoryContract);
      this.updateListStores();
      this.formPhysicalInventory.controls['consecutive'].disable();
      this.location.replaceState(`/physical-inventory/register`);
    } else {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para crear inventarios físicos. Comuníquese con el administrador del sistema.'
      );
    }
  }

  async deletePhysicalInventory() {
    if (this.physicalInventoryContract.physicalInventoryId != 0 && this.physicalInventoryContract.physicalInventoryId != null) {
      if (this.permissionUpdatable) {
        if (this.physicalInventoryContract.physicalInventoryStateId === PhysicalInventoryStateEnums.open) {
          const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar el inventario físico actual?');

          if (accepted) {
            try {
              await firstValueFrom(this.physicalInventoryService.deletePhysicalInventory(this.physicalInventoryContract.physicalInventoryId));
              this.createNewRegister();
              this.toastHandlerService.showToast(
                'success',
                'El inventario físico fue eliminado exitosamente.'
              );
            } catch (error) {
              console.error("Error al eliminar el inventario físico: ", error);
            }
          }
        } else {
          this.toastHandlerService.showToast(
            'warning',
            'Para eliminar el inventario físico debe estar en estado Abierto.'
          );
        }
      } else {
        this.toastHandlerService.showToast(
          'warning',
          'El usuario no tiene permisos para eliminar inventario físico. Comuníquese con el administrador del sistema.'
        );
        return;
      }
    } else {
      this.toastHandlerService.showToast(
        'warning',
        'Debe crear un inventario físico.'
      );
      return;
    }
  }

  async getButtonClick(buttonClick: any) {
    const inventoryId = this.formPhysicalInventory.get("inventoryId")?.value;
    const branchId = this.formPhysicalInventory.get("branchId")?.value;
    const storeId = this.formPhysicalInventory.get("storeId")?.value;
    const consecutive = this.formPhysicalInventory.get("consecutive")?.value;

    if (inventoryId == null || inventoryId == 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Seleccione un inventario.'
      );
      return;
    }
    if (branchId == null || branchId == 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Seleccione una sucursal.'
      );
      return;
    }
    if (storeId == null || storeId == 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Seleccione una bodega.'
      );
      return;
    }

    switch (buttonClick.target.id) {
      case 'first': {
        try {
          this.physicalInventoryContract = await firstValueFrom(
            this.physicalInventoryService.getFirstPhysicalInventoryByKeysAsync(inventoryId, branchId, storeId)
          );
          this.physicalInventoryId = this.physicalInventoryContract.physicalInventoryId;
          this.validateForm(this.physicalInventoryContract);
          this.location.replaceState(`/physical-inventory/register/${this.physicalInventoryId}`);
        } catch (error) {
          this.createNewRegister();
          console.error('Error al obtener el primer inventario físico por claves.', error);
        }
        break;
      }
      case 'previus': {
        try {
          this.physicalInventoryContract = await firstValueFrom(
            this.physicalInventoryService.getPreviusPhysicalInventoryByKeysAsync(inventoryId, branchId, storeId, consecutive)
          );
          this.physicalInventoryId = this.physicalInventoryContract.physicalInventoryId;
          this.validateForm(this.physicalInventoryContract);
          this.location.replaceState(`/physical-inventory/register/${this.physicalInventoryId}`);
        } catch (error) {
          this.createNewRegister();
          console.error('Error al obtener el anterior inventario físico por claves.', error);
        }
        break;
      }
      case 'next': {
        try {
          this.physicalInventoryContract = await firstValueFrom(
            this.physicalInventoryService.getNextPhysicalInventoryByKeysAsync(inventoryId, branchId, storeId, consecutive)
          );
          this.physicalInventoryId = this.physicalInventoryContract.physicalInventoryId;
          this.validateForm(this.physicalInventoryContract);
          this.location.replaceState(`/physical-inventory/register/${this.physicalInventoryId}`);
        } catch (error) {
          this.createNewRegister();
          console.error('Error al obtener el siguiente inventario físico por claves.', error);
        }
        break;
      }
      case 'last': {
        try {
          this.physicalInventoryContract = await firstValueFrom(
            this.physicalInventoryService.getLastPhysicalInventoryByKeysAsync(inventoryId, branchId, storeId)
          );
          this.physicalInventoryId = this.physicalInventoryContract.physicalInventoryId;
          this.validateForm(this.physicalInventoryContract);
          this.location.replaceState(`/physical-inventory/register/${this.physicalInventoryId}`);
        } catch (error) {
          this.createNewRegister();
          console.error('Error al obtener el último inventario físico por claves.', error);
        }
        break;
      }
    }
  }

  async getPermission() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.getPermissionsForUserByWindow();
    } catch (error) {
      console.error('Error al obtener el perfil de permisos por URL.', error);
    }
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(this.userAuthenticatedService.userObservable.pipe(filter(user => user && user.userId !== 0)));
    this.permissionsUser = await firstValueFrom(this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.PhysicalInventory));
  }

  async getPhysicalInventoryById() {
    try {
      this.physicalInventoryContract = await firstValueFrom(this.physicalInventoryService.getPhysicalInventoryById(this.physicalInventoryId));
      this.location.replaceState(`/physical-inventory/register/${this.physicalInventoryId}`);
    } catch (error) {
      console.error('Error al obtener el corte por Id.', error);
    }
  }

  async handleInitialState() {
    if (this.physicalInventoryId !== null && this.physicalInventoryId !== 0) {
      await this.getPhysicalInventoryById();
      if (this.physicalInventoryContract.physicalInventoryId !== 0) {
        this.formPhysicalInventory.enable();
        this.validateForm(this.physicalInventoryContract);
      }
    } else {
      this.createNewRegister();
    }
  }

  async loadLists() {
    try {
      const [inventories, branches] = await Promise.all([
        firstValueFrom(this.inventoryService.getAllInventoriesForSelectWithOutSubrentalAsync()),
        firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
      ]);
      this.listInventories = inventories;
      this.listBranches = branches;
      this.formPhysicalInventory.controls['inventoryId'].patchValue(this.listInventories[0].value);
      this.formPhysicalInventory.controls['branchId'].patchValue(this.listBranches[0].value);
      this.updateListStores();
    } catch (error) {
      console.error('Error al cargar listas de inventario físico.', error);
    }
  }

  async openAdjustExcessModal() {
    if (this.physicalInventoryContract.physicalInventoryId != 0 && this.physicalInventoryContract.physicalInventoryId != null) {
      this.adjustExcessModalComponent && this.adjustExcessModalComponent.show();
    } else {
      this.toastHandlerService.showToast('warning', 'Debe crear un inventario físico.', 'Atención');
    }
  }

  async send() {
    Object.keys(this.formPhysicalInventory.controls).forEach(key => {
      const control = this.formPhysicalInventory.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formPhysicalInventory.valid) {
      const model = this.formPhysicalInventory.getRawValue();
      if (model.dateAdjustment < model.datePhysicalInventory) {
        this.toastHandlerService.showToast('error', 'La Fecha de Ajuste no puede ser menor a la Fecha de Inventario.', 'Atención');
        return;
      }
      try {
        if (this.physicalInventoryContract.physicalInventoryId == 0) {
          this.physicalInventoryContract = await firstValueFrom(this.physicalInventoryService.createPhysicalInventory(model));
          this.physicalInventoryId = this.physicalInventoryContract.physicalInventoryId;
          this.formPhysicalInventory.patchValue({ consecutive: this.physicalInventoryContract.consecutive });
          this.formPhysicalInventory.controls['consecutive'].enable();
          this.toastHandlerService.showToast('success', 'Inventario físico creado exitosamente.');
          this.location.replaceState(`/physical-inventory/register/${this.physicalInventoryId}`);
        } else {
          this.physicalInventoryContract.datePhysicalInventory = model.datePhysicalInventory;
          this.physicalInventoryContract.dateAdjustment = model.dateAdjustment;
          this.physicalInventoryContract.observation = model.observation;
          this.physicalInventoryContract.physicalInventoryStateId = model.physicalInventoryStateId;
          this.physicalInventoryContract = await firstValueFrom(this.physicalInventoryService.updatePhysicalInventory(this.physicalInventoryContract));
          this.toastHandlerService.showToast('success', 'Inventario físico actualizado exitosamente.');
        }
        this.updateFormState();
      } catch (error) {
        console.error('Error al guardar inventario físico.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      observation: {
        maxlength: 'El campo Observación no puede tener más de 200 caracteres.'
      },
      physicalInventoryStateId: {
        required: 'El Estado del Inventario Físico es obligatorio.'
      },
      dateAdjustment: {
        required: 'La Fecha de Ajuste es obligatoria.'
      },
      datePhysicalInventory: {
        required: 'La Fecha de Inventario es obligatoria.'
      },
      consecutive: {
        required: 'El campo Consecutivo es obligatorio.'
      },
      storeId: {
        required: 'El campo Bodega es obligatorio.',
        min: 'Debes seleccionar una Bodega válida.'
      },
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debes seleccionar una Sucursal válida.'
      },
      inventoryId: {
        required: 'El campo Inventario es obligatorio.',
        min: 'Debes seleccionar un Inventario válido.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formPhysicalInventory.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updateListStores() {
    const branchId = this.formPhysicalInventory.controls['branchId'].value;
    const inventoryId = this.formPhysicalInventory.controls['inventoryId'].value;

    if (branchId && inventoryId) {
      try {
        this.listStores = await firstValueFrom(this.storeService.getStoresActivesByBranchInventoryForSelect(branchId, inventoryId));
      } catch (error) {
        this.listStores = [];
        console.error('Error al cargar bodegas por sucursal.', error);
      }
    } else {
      this.listStores = [];
    }
  }

  updateFormState() {
    if (this.state() === PhysicalInventoryStateEnums.close) {
      this.disabledChangeState = true;
      this.disabledDetail = true;
      this.formPhysicalInventory.controls['datePhysicalInventory'].disable();
      if (this.physicalInventoryContract.swExcessAdjustment && this.physicalInventoryContract.swMissingAdjustment) {
        this.disabledSave = true;
        this.formControlStateService.setControlState(this.formPhysicalInventory, ['dateAdjustment', 'observation'], false);
      }
    }
    if (this.physicalInventoryContract.physicalInventoryId !== 0 &&
      this.physicalInventoryContract.physicalInventoryId !== null) {
      if (this.state() === PhysicalInventoryStateEnums.open) {
        this.disabledDetail = false;
        this.disabledChangeState = false;
        this.disabledSave = false;
      }
    }
    if (this.physicalInventoryContract.swExcessAdjustment) {
      this.disabledAdjustExcessBtn = true;
    }
    if (this.physicalInventoryContract.swMissingAdjustment) {
      this.disabledAdjustMissingBtns = true;
    }
  }

  async validateForm(physicalInventoryModel: PhysicalInventoryModel) {
    this.formPhysicalInventory = this.formBuilder.group({
      inventoryId: [physicalInventoryModel.inventoryId, [Validators.required, Validators.min(1)]],
      branchId: [physicalInventoryModel.branchId, [Validators.required, Validators.min(1)]],
      storeId: [physicalInventoryModel.storeId, [Validators.required, Validators.min(1)]],
      consecutive: [physicalInventoryModel.consecutive, [Validators.required]],
      datePhysicalInventory: [physicalInventoryModel.datePhysicalInventory ? this.dateConverterService.convertDatePreserveDay(physicalInventoryModel.datePhysicalInventory) : null, [Validators.required]],
      dateAdjustment: [physicalInventoryModel.dateAdjustment ? this.dateConverterService.convertDatePreserveDay(physicalInventoryModel.dateAdjustment) : null, [Validators.required]],
      physicalInventoryStateId: [physicalInventoryModel.physicalInventoryStateId, [Validators.required, Validators.min(1)]],
      observation: [physicalInventoryModel.observation, [Validators.maxLength(200)]]
    });
    if (physicalInventoryModel.physicalInventoryStateId === PhysicalInventoryStateEnums.close) {
      this.disabledAdjustExcessBtn = false;
      this.disabledAdjustMissingBtns = false;
    }
    if (physicalInventoryModel.physicalInventoryId !== 0 && physicalInventoryModel.physicalInventoryId !== null) {
      this.inputsForAdjustExcessModals$.next({
        branchId: physicalInventoryModel.branchId,
        inventoryId: physicalInventoryModel.inventoryId,
        storeId: physicalInventoryModel.storeId
      });
    }
    this.state.set(physicalInventoryModel.physicalInventoryStateId);
    this.updateFormState();
  }

}
